import { Directive, Output, EventEmitter, HostListener, Renderer2, OnDestroy, ElementRef, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FileValidationService, FileValidationOptions, DragValidationResult } from '../services/file-validation.service';
import { DragDropVisualUtils, DragDropOverlayUtils } from '../utilities/drag-drop.utilities';

@Directive({
    selector: '[appScopedDragDrop]',
    standalone: false
})
export class ScopedDragDropDirective implements OnDestroy {
  @Output() filesDropped: EventEmitter<FileList> = new EventEmitter<FileList>();
  @Output() dragStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() allowedTypes?: string[];

  private dragCounter = 0;
  private overlay: HTMLElement | null = null;
  private currentValidation: DragValidationResult = { isValid: true };
  private cleanupTimeout?: number;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private fileValidationService: FileValidationService,
    private translateService: TranslateService
  ) {}

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;

    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = undefined;
    }

    if (this.dragCounter === 1) {
      this.validateDraggedFiles(event);
      this.createOverlay();
      this.dragStateChanged.emit(true);
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    event.preventDefault();

    this.validateDraggedFiles(event);

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.currentValidation.isValid ? 'copy' : 'none';
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);

    if (this.dragCounter === 0) {
      this.cleanupTimeout = window.setTimeout(() => {
        if (this.dragCounter === 0) {
          this.resetValidationState();
          this.removeOverlay();
          this.dragStateChanged.emit(false);
        }
        this.cleanupTimeout = undefined;
      }, 50);
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter = 0;

    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = undefined;
    }

    this.resetValidationState();
    this.removeOverlay();
    this.dragStateChanged.emit(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.filesDropped.emit(files);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.overlay && this.dragCounter === 0) {
      this.cleanupTimeout = window.setTimeout(() => {
        this.resetValidationState();
        this.removeOverlay();
        this.dragStateChanged.emit(false);
        this.cleanupTimeout = undefined;
      }, 100);
    }
  }

  private createOverlay(): void {
    if (this.overlay) {
      return;
    }

    // Get the host element's position and dimensions
    const hostElement = this.elementRef.nativeElement;
    const rect = hostElement.getBoundingClientRect();

    this.overlay = this.renderer.createElement('div');
    this.renderer.addClass(this.overlay, 'scoped-drag-overlay');
    this.renderer.setStyle(this.overlay, 'position', 'fixed');
    this.renderer.setStyle(this.overlay, 'top', `${rect.top}px`);
    this.renderer.setStyle(this.overlay, 'left', `${rect.left}px`);
    this.renderer.setStyle(this.overlay, 'width', `${rect.width}px`);
    this.renderer.setStyle(this.overlay, 'height', `${rect.height}px`);
    DragDropOverlayUtils.updateOverlayVisualState(this.overlay, this.renderer, this.currentValidation);
    this.renderer.setStyle(this.overlay, 'z-index', '9999');
    this.renderer.setStyle(this.overlay, 'pointer-events', 'none');
    this.renderer.setStyle(this.overlay, 'display', 'flex');
    this.renderer.setStyle(this.overlay, 'align-items', 'center');
    this.renderer.setStyle(this.overlay, 'justify-content', 'center');

    // Create the content container
    const contentContainer = this.renderer.createElement('div');
    this.renderer.setStyle(contentContainer, 'text-align', 'center');
    this.renderer.setStyle(contentContainer, 'display', 'flex');
    this.renderer.setStyle(contentContainer, 'flex-direction', 'column');
    this.renderer.setStyle(contentContainer, 'align-items', 'center');
    this.renderer.setStyle(contentContainer, 'gap', '8px');

    // Create icon and text using utilities
    const icon = DragDropOverlayUtils.createOverlayIcon(this.renderer);
    const textContent = DragDropVisualUtils.getDragMessage(this.currentValidation, this.translateService);
    const mainText = DragDropOverlayUtils.createOverlayText(this.renderer, textContent);

    // Assemble the overlay
    this.renderer.appendChild(contentContainer, icon);
    this.renderer.appendChild(contentContainer, mainText);
    this.renderer.appendChild(this.overlay, contentContainer);
    this.renderer.appendChild(document.body, this.overlay);
  }



  private removeOverlay(): void {
    if (this.overlay) {
      this.renderer.removeChild(document.body, this.overlay);
      this.overlay = null;
    }
  }

  ngOnDestroy(): void {
    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = undefined;
    }
    this.removeOverlay();
  }

  private validateDraggedFiles(event: DragEvent): void {
    if (!event.dataTransfer) {
      this.resetValidationState();
      return;
    }

    const validationOptions: FileValidationOptions = {
      allowedTypes: this.allowedTypes,
      showErrors: false
    };

    const dataToValidate = event.dataTransfer.items || event.dataTransfer.files;
    this.currentValidation = this.fileValidationService.validateDragFiles(
      dataToValidate,
      validationOptions
    );

    if (this.overlay) {
      this.updateOverlayVisualState();
    }
  }

  private updateOverlayVisualState(): void {
    if (!this.overlay) return;

    DragDropOverlayUtils.updateOverlayVisualState(this.overlay, this.renderer, this.currentValidation);

    const message = DragDropVisualUtils.getDragMessage(this.currentValidation, this.translateService);
    DragDropOverlayUtils.updateOverlayText(this.overlay, this.renderer, message);
  }



  private resetValidationState(): void {
    this.currentValidation = { isValid: true };
  }
}
